import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { useLocation, useSearchParams } from 'react-router'
import { Card, Row, Table, TableScroll, Td, Th } from '../../ui'
import { FINDING_REFERENCE, getFindings, getOverview, getReportSummary } from '../../lib/api'
import type { ApiResult, AssessmentChanges, FindingsPage, FindingSummary, ScanFreshness } from '../../lib/api'
import { daysOpen, FindingsInsights, isQuickWin, passedTarget, type Focus } from './FindingsInsights'
import {
  anyFilter, facetOptions, FILTER_KEYS, leadFirst, OTHER_PROJECT, OWNER_NONE, passes, projectOf, readFilters,
} from './findingFilters'
import type { FilterKey, ListFilters } from './findingFilters'
import { Freshness } from './Freshness'
import { FindingScope } from './FindingScope'
import {
  controlCount, controls as controlsWord, listOf, noLicenceFor, notAssessedAreasSentence,
  notLicensedIn, serviceNames, wholeAreaNotLicensed,
} from './notLicensed'
import { severityTag } from './LiveDashboard'
import { agrees, SESSION_CHANGED, SESSION_UNKNOWN, useSession } from './session'
import { describeFailure, useApi } from './useApi'
import { severitySourceLabel } from './labels'
import './live.css'
import './findings-pages.css'

const SEVERITY_FILTERS = ['Critical', 'High', 'Medium', 'Low'] as const

/** Rows per request: the server's maximum, so most tenants are answered in one. */
const REQUEST_SIZE = 100

/** Requests per query, the first included -- 1000 findings. Past that the list says it stopped. */
const MAX_REQUESTS = 10

/** Rows per page tab. */
const PAGE_ROWS = 25

/**
 * How the list is ordered. "check" is By check (findings pages, slice 2): the severity order from the
 * server, then one row per check with the resources it affects, grouped here over what is loaded.
 */
type Order = 'lastSeen' | 'severity' | 'impact' | 'check'

/** Where one check runs on many resources, By check is where the page opens (owner, 2026-10-11). */
const GROUPED_BY_CHECK = new Set(['Cloud', 'Endpoint'])

/** What the server is asked. The search box is not in it: it filters what has been loaded. */
interface FindingsQuery {
  status: 'active' | 'suppressed'
  severity?: string
  sort: 'lastSeen' | 'severity' | 'impact'
  area?: string
}

/** Page 1, carrying the query that produced it, so every page after it is asked the same thing. */
type FirstPage = FindingsPage & { query: FindingsQuery }

/** One string per distinct question asked of the server: a poll asks the same one again. */
function keyOf(query: FindingsQuery): string {
  return `${query.status}|${query.severity ?? ''}|${query.sort}|${query.area ?? ''}`
}

/** The pages fetched after page 1, and WHICH page 1 they follow. */
interface Tail {
  of: FirstPage | null
  /** keyOf the query those pages answer. */
  key: string | null
  items: FindingSummary[]
  loading: boolean
  /** Re-reading the same query after a poll: the rows already shown stay until the new ones are all in. */
  refreshing: boolean
  /** The request cap was reached with the server still holding a cursor: the list is not whole. */
  capped: boolean
  failed: number | null
}

const NO_TAIL: Tail = {
  of: null, key: null, items: [], loading: false, refreshing: false, capped: false, failed: null,
}

/**
 * The findings list, from the real API.
 *
 * Freshness renders above the list, and that is not decoration. "No open findings match this
 * filter" and "this tenant is clean" are the same sentence to a reader, so the state of the scan
 * has to be on this page and not only on the dashboard — somebody who arrives here from a link
 * never sees the dashboard at all.
 *
 * The server pages by cursor, and the cursor is the server's — opaque, signed, and bound to the
 * workspace, sort and filter it was issued for. A cursor cannot jump to page 3 and there is no total
 * to number pages from, so the list follows the cursors to the end (up to a cap) and draws numbered
 * page tabs over what it holds. The client's only job with a cursor is to hand back what it was
 * given; one that is forged, expired or mismatched is refused with a 400 rather than answered with
 * page one, which is what made an expired cursor show the first page twice.
 */
/**
 * @param area
 * Restricts the list to one posture area — Identity, Endpoint, Cloud. Omitted on the
 * Recommendations page, which is deliberately everything at once.
 *
 * The server already filtered by area and the client already had the parameter; nothing passed
 * it, so the three posture pages sat as placeholders telling customers "no tenant has been
 * scanned" over a tenant that had been.
 */
export function LiveFindings({
  onOpen,
  area,
  areaTile,
}: {
  onOpen: (finding: FindingSummary) => void
  area?: string
  /** The area's own Microsoft figure (Identity's Secure Score, Endpoint's exposure score, Cloud's
   *  subscriptions), drawn as the last tile of the insights strip. */
  areaTile?: ReactNode
}) {
  const session = useSession()
  const [severity, setSeverity] = useState<string | null>(null)

  // What the insights strip narrowed the list to (past target, quick wins), over the loaded rows.
  const [focus, setFocus] = useState<Focus>(null)


  // Active findings by default. The other view is everything a person has set aside — suppressed,
  // not-applicable and accepted risks all land in the 'suppressed' status — so a decision can be
  // found again and reopened rather than being gone the moment it is made.
  // The dashboard's "Open the Set-aside list" arrives as ?view=set-aside.
  const [view, setView] = useState<'active' | 'suppressed'>(
    () => (new URLSearchParams(window.location.search).get('view') === 'set-aside' ? 'suppressed' : 'active'),
  )

  // The search box, by NAME, applied to the rows already loaded as it is typed. In memory, so there
  // is no request per keystroke -- the reason the old reference box waited for Enter, since seven of
  // eight prefix requests answered nothing anybody asked and could land after the eighth.
  //
  // It can arrive in the address: the header search sends people here as /recommendations?q=mfa.
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const asked = searchParams.get('q')
  const [typed, setTyped] = useState(asked ?? '')

  // Area, project, owner and standard, from the address and kept there (findingFilters.ts). The
  // address is the only copy: a select writes it, and the list reads it back, so there is no second
  // state to fall out of step with a link, a reload or the back button.
  const filters = readFilters(searchParams, area)

  // Project, owner and standard sit behind "Filters": open when the page arrives filtered, and once
  // opened they stay, so clearing them does not take the selects away from under the keyboard.
  const [filtersOpen, setFiltersOpen] = useState(() => anyFilter(filters))

  // Recommendations (no area) leads with the grouped impact view; a posture page opens on By
  // severity, a plain table, with By project beside it for the grouped one.
  const [sort, setSort] = useState<Order>(area ? (GROUPED_BY_CHECK.has(area) ? 'check' : 'severity') : 'impact')

  // Which checks are opened to show their resources, By check.
  const [openChecks, setOpenChecks] = useState<Record<string, boolean>>({})

  // The main Recommendations view (no single area) leads with impact × effort, and that is where
  // grouping earns its place: seventy rows read as a couple of dozen jobs. The two flat sorts stay a
  // plain table. Grouping client-side is honest because the list loads everything the filter matches
  // before grouping it, and says so when the cap stops it short. There, the Set-aside view is never
  // grouped: a "do these first / worth clearing" band over findings a person has deliberately set
  // aside would contradict the decision they just made.
  //
  // An area page's By project is the same impact order, grouped the same way, with no "Start here"
  // band -- so its Set-aside view may be grouped too: a project heading over set-aside findings says
  // which job they belong to, not that they are worth clearing (codex review, 2026-09-27: six Email
  // authentication findings should read as one project, not six rows).
  const grouped = sort === 'impact' && (area !== undefined || view === 'active')

  // Which projects are open. Collapsed at first, each showing its most important finding and its
  // count, so sixty-five findings read as a page of projects. A search or a filter opens them all:
  // somebody who narrowed the list wants to see what matched, not a lead row and a button. A
  // project a person opened or closed stays as they left it.
  const [opened, setOpened] = useState<Record<string, boolean>>({})
  const ids = useId()

  // Which query the rows on screen belong to. Clearing state was not enough on its own: a page
  // already in flight when the filter changed came back afterwards and appended its rows to the new
  // list, mixing two queries with nothing on screen to say so. Every follow-up page now checks that
  // its generation is still the current one before it is allowed to land, and stops asking if not.
  const generation = useRef(0)

  // Same reasoning as the dashboard: this page shows the freshness card too, and a findings list
  // that keeps saying "assessment running" after it stopped is the more misleading of the two --
  // the list underneath is incomplete and says so, so it must stop saying so when it is not.
  //
  // `rescans` is bumped when the freshness card queues an assessment. Without it the list never
  // asked again after a queue from this page, so it never showed the queued or running state and
  // never started the poll that follows one -- the dashboard did, this page did not.
  const [rescans, setRescans] = useState(0)
  const first = useApi<FirstPage>(
    async (): Promise<ApiResult<FirstPage>> => {
      const query: FindingsQuery = { status: view, severity: severity ?? undefined, sort: sort === 'check' ? 'severity' : sort, area }
      // The server's maximum from the first request, in every view: most tenants are then one
      // request, and the pages after it are only for the few that are not.
      const result = await getFindings({ ...query, pageSize: REQUEST_SIZE })
      return result.ok ? { ok: true, data: { ...result.data, query } } : result
    },
    [severity, sort, area, view, rescans],
    (data) => data.freshness.status === 'running' || data.freshness.scanPending,
    // KEEP THE LIST MOUNTED while a new filter loads. Without this every filter, sort or search
    // click dropped to "Loading…", unmounting the filter bar -- the button just pressed included --
    // so keyboard focus fell to the page and a screen-reader user had to tab back through the whole
    // shell after every change. `pending` below says the rows on screen are the previous query's.
    true,
  )
  const firstReady = first.state === 'ready' ? first.data : null

  // The insights strip's figures and the severity counts: the server's overview, the same snapshot the
  // dashboard reads; and what the last assessment changed, the report's own read. Both supplementary:
  // the list stands without them.
  const overview = useApi(() => getOverview(), [rescans], undefined, true)
  const summary = useApi(() => getReportSummary(), [rescans], undefined, true)

  // True from a filter change until its answer lands: the rows on screen belong to the previous
  // query. Announced, the page tabs held (paging through the old query's rows is not what was
  // asked for), and the empty-state sentence held back (it would describe the new filter over the
  // old rows).
  const [pending, setPending] = useState(false)

  const [tail, setTail] = useState<Tail>(NO_TAIL)
  // The tail as last committed, for the effect below to tell a poll from a new query. Declared
  // before it, so it has run by the time that one reads it.
  const tailNow = useRef(tail)
  useEffect(() => {
    tailNow.current = tail
  })

  // The query whose pages are being followed right now, or null. See the effect below.
  const following = useRef<string | null>(null)

  // The page shown, tied to the QUERY it indexes. A new filter or sort brings page 1 of the list -- in
  // the same render, rather than one frame of "page 3" of a different list. A poll does not: it asks
  // the same question again every few seconds while an assessment runs, and sending somebody reading
  // page 3 back to page 1 each time made the list unreadable during a scan.
  const [paging, setPaging] = useState<{ key: string | null; index: number }>({ key: null, index: 0 })

  const list = useRef<HTMLDivElement>(null)
  const searchBox = useRef<HTMLInputElement>(null)
  const facetBar = useRef<HTMLDivElement>(null)

  // A search handed over in the address is adopted on every navigation that carries one, not only
  // on mount: searching from the header while already on this page is a new navigation to the same
  // page, and the box must not keep the search it had.
  useEffect(() => {
    if (asked === null) return
    setTyped(asked)
    setPaging({ key: null, index: 0 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key])

  // Once the box is edited, the address stops claiming a search the box no longer shows. Dropped
  // rather than kept in step: an address rewritten on every keystroke lands behind the typing
  // (router updates are transitions) and the effect above would hand an older search back to the box.
  function forgetAsked() {
    if (asked === null) return
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current)
        next.delete('q')
        return next
      },
      { replace: true },
    )
  }

  function requery(next: {
    severity?: string | null
    sort?: Order
    view?: 'active' | 'suppressed'
  }) {
    // Nothing to ask. Re-pressing the lit pill changes no query, so no fetch runs -- and the wait
    // below was then never ended: "Updating…" stuck on, and the list held for good.
    const changed =
      (next.severity !== undefined && next.severity !== severity) ||
      (next.sort !== undefined && next.sort !== sort) ||
      (next.view !== undefined && next.view !== view)
    if (!changed) return

    // Pages still arriving for the old query are dropped, and no more are asked for.
    generation.current += 1
    following.current = null
    setPending(true)
    setTail((t) => ({ ...t, loading: false, failed: null }))
    setPaging({ key: null, index: 0 })

    if (next.severity !== undefined) setSeverity(next.severity)
    if (next.sort !== undefined) setSort(next.sort)
    if (next.view !== undefined) setView(next.view)
  }

  // When page 1 lands -- a fresh query or a poll -- fetch the rest by following its cursor, one page
  // after another, up to the cap. Pages accumulated after an older page 1 are not appended to it:
  // they were fetched against the previous keyset snapshot and no longer abut this one, so appending
  // would stitch two snapshots -- hiding a row, or colliding two React keys on the same id.
  //
  // A POLL (the same query asked again) is the one exception to clearing them first: the rows already
  // on screen stay, under the new page 1, until the new pages are all in, and are then replaced in one
  // step. Clearing them made the list shrink to one page and regrow every four seconds during a scan.
  // For those few seconds a finding can show twice across the seam (distinct() drops the repeat) or
  // be missed until the swap -- a moment, where clearing was a flicker on every poll.
  //
  // And a poll that lands while this same list's pages are STILL being followed lets that finish
  // rather than starting over. Starting over on every poll meant a list slower to follow than the
  // four-second poll never finished at all: it sat on the rows of the last full read, quietly, for as
  // long as the assessment ran. The pages it brings follow an older page 1 than the one now shown --
  // the same seam the swap already accepts -- and the next poll after it finishes reads afresh.
  useEffect(() => {
    setPending(false)
    if (firstReady === null) return

    const page1 = firstReady
    const key = keyOf(page1.query)
    const previous = tailNow.current

    if (following.current === key && previous.key === key) {
      setTail((t) => (t.key === key ? { ...t, of: page1 } : t))
      return
    }

    // Whatever was being followed before -- another query's pages -- stops here.
    generation.current += 1
    const mine = generation.current
    const refreshing = previous.key === key && page1.nextCursor !== null
    setTail({
      of: page1,
      key,
      items: refreshing ? previous.items : [],
      loading: page1.nextCursor !== null,
      refreshing,
      capped: refreshing ? previous.capped : false,
      failed: null,
    })

    if (page1.nextCursor === null) {
      following.current = null
      return
    }
    following.current = key

    async function rest() {
      let cursor = page1.nextCursor
      let requests = 1
      const collected: FindingSummary[] = []

      while (cursor !== null && requests < MAX_REQUESTS) {
        const result = await getFindings({ ...page1.query, pageSize: REQUEST_SIZE, cursor })
        requests += 1

        // The filter changed or the list unmounted while this was in flight. These rows belong
        // to a list that is no longer on screen.
        if (mine !== generation.current) return

        if (!result.ok) {
          // 400 means the server refused the cursor — expired, or issued for a query this no
          // longer is. Said out loud rather than swallowed: silently stopping looks like the end
          // of the list, which is the one thing a findings list must never imply by accident.
          const status = result.status
          following.current = null
          setTail((t) =>
            t.key === key
              ? { ...t, items: refreshing ? collected : t.items, loading: false, refreshing: false, failed: status }
              : t)
          return
        }

        collected.push(...result.data.items)
        // A fresh query shows its rows as they arrive; a poll holds them for the swap below.
        if (!refreshing) {
          const items = result.data.items
          setTail((t) => (t.key === key ? { ...t, items: [...t.items, ...items] } : t))
        }
        cursor = result.data.nextCursor
      }

      const stoppedShort = cursor !== null
      following.current = null
      setTail((t) =>
        t.key === key
          ? {
              ...t,
              items: refreshing ? collected : t.items,
              loading: false,
              refreshing: false,
              capped: stoppedShort,
            }
          : t)
    }

    void rest()
  }, [firstReady])

  // Leaving the page stops whatever is still being followed.
  useEffect(
    () => () => {
      generation.current += 1
      following.current = null
    },
    [],
  )

  // A refused re-query replaces the page with its error, which ends the wait too.
  const failed = first.state === 'error'
  useEffect(() => {
    if (failed) setPending(false)
  }, [failed])

  if (first.state === 'loading') return <div className="live-empty">Loading…</div>

  if (first.state === 'error') {
    return <div className="live-error">{describeFailure(first.status)}</div>
  }

  // The chrome around this card carries a workspace name the shell fetched earlier. If the session
  // has moved since — a second tab signing into another workspace replaces the shared cookie —
  // these findings belong to a different tenant than the name above them.
  if (!agrees(session, first.data.workspaceId)) {
    return (
      <div className="live-error">
        {session.state === 'unknown' ? SESSION_UNKNOWN : SESSION_CHANGED}
      </div>
    )
  }

  const data = first.data

  // The pages after page 1 count only for the query they answer. A poll of the same query keeps them
  // on screen until its own are in (see the effect above); a different query never shows them.
  const key = keyOf(data.query)
  const own = tail.key === key
  const loadingRest = own ? tail.loading && !tail.refreshing : data.nextCursor !== null
  const capped = own && tail.capped
  const moreFailed = own && tail.of === data ? tail.failed : null

  const items = distinct([...data.items, ...(own ? tail.items : [])])

  const searched = typed.trim()
  const matches = matcherFor(searched)
  const filtered = anyFilter(filters)
  // Only an answer that is an overview, for this workspace: anything else and the strip stays away.
  const overviewData =
    overview.state === 'ready' && agrees(session, overview.data.workspaceId) &&
    overview.data.activeBySeverity && overview.data.activeByArea
      ? overview.data
      : null
  const targets = overviewData?.remediationTargetDays
  const changes: AssessmentChanges | null | 'unavailable' =
    summary.state === 'ready' && agrees(session, summary.data.workspaceId) && summary.data.changes !== undefined
      ? summary.data.changes
      : 'unavailable'
  // What the latest assessment added or brought back, marked "New" on its row.
  const fresh = new Set(
    typeof changes === 'object' && changes?.previous ? [...changes.new.items, ...changes.reopened.items].map((c) => c.id) : [],
  )
  const focused = (f: FindingSummary) =>
    focus === 'pastTarget' ? passedTarget(f, targets) !== null : focus === 'quickWins' ? isQuickWin(f) : true
  const shown = items.filter((f) => (matches ? matches(f) : true) && passes(f, filters) && focused(f))
  // The severity chips' counts: the server's, for this area or every area.
  const severityCounts: Record<string, number> | null = overviewData
    ? area ? overviewData.activeByAreaAndSeverity?.[area] ?? {} : overviewData.activeBySeverity
    : null

  // By check, the page tabs count checks, each with its resources beneath it.
  const checks = sort === 'check' ? groupByCheck(shown) : null
  const pageCount = Math.max(1, Math.ceil((checks ? checks.length : shown.length) / PAGE_ROWS))
  const current = Math.min(paging.key === key ? paging.index : 0, pageCount - 1)
  const rows = shown.slice(current * PAGE_ROWS, (current + 1) * PAGE_ROWS)
  const checkRows = checks ? checks.slice(current * PAGE_ROWS, (current + 1) * PAGE_ROWS) : null
  const paged = !grouped && pageCount > 1

  function goTo(index: number, from: 'top' | 'bottom') {
    if (index === current || index < 0 || index >= pageCount) return
    setPaging({ key, index })

    // To the new rows. Focus on the table region, which names the page, so a keyboard or
    // screen-reader user lands on what changed rather than on a tab the rows moved away from. Only
    // the bottom tabs scroll: from the top ones the table is already right there, and jumping would
    // scroll the filters away for nothing. jsdom has no scrollIntoView.
    const region = list.current?.querySelector<HTMLElement>('[role="region"]')
    if (!region) return
    region.focus({ preventScroll: true })
    if (from === 'bottom' && typeof region.scrollIntoView === 'function') {
      region.scrollIntoView({ block: 'start' })
    }
  }

  function clearSearch() {
    setTyped('')
    setPaging({ key, index: 0 })
    forgetAsked()
    searchBox.current?.focus()
  }

  // A filter is written to the address and read back from it. Replaced, not pushed: the back button
  // leaves the page rather than stepping back through every select, as the dropped ?q= does. A new
  // filter starts on its own first page -- page 3 of the last rows may not exist among these.
  function setFilter(which: FilterKey, value: string | null) {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current)
        if (value === null) next.delete(which)
        else next.set(which, value)
        return next
      },
      { replace: true },
    )
    setPaging({ key, index: 0 })
  }

  // Clears the four filters and nothing else: the search box and the severity pills say for
  // themselves what they hold, and each has its own way back.
  function clearFilters() {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current)
        for (const which of FILTER_KEYS) next.delete(which)
        return next
      },
      { replace: true },
    )
    setPaging({ key, index: 0 })
    // The button pressed goes away with the filters. Focus goes to the first filter, which stays,
    // rather than falling to the page -- or to the search box when the bar goes too.
    const first = items.length > 0 ? facetBar.current?.querySelector('select') : null
    ;(first ?? searchBox.current)?.focus()
  }

  // The quick wins: high-impact and cheap, from whatever is loaded (and searched for, and filtered
  // to). Derived from the impact-sorted list rather than a second request — they are already near
  // the top of it. Capped, because a "start here" list of thirty is not a start. Recommendations
  // only: an area page's By project has no band of its own.
  const quickWins = grouped && !area
    ? shown
        .filter((f) => (f.severity === 'Critical' || f.severity === 'High') && f.effort === 'Low')
        .slice(0, 6)
    : []

  const groups = grouped ? groupByWorkstream(shown) : []

  // Every project open by default once the list is narrowed (see `opened` above).
  const narrowed = matches !== null || filtered || focus !== null
  const isOpen = (name: string) => opened[name] ?? narrowed
  const toggle = (name: string) => setOpened((now) => ({ ...now, [name]: !(now[name] ?? narrowed) }))

  // The filter bar shows once there is something to narrow, or a filter to take off.
  const showFacets = items.length > 0 || filtered

  // An owner or a standard is offered only when the server sent the field: from an older server
  // every row would match neither value, and a filter that can only ever empty the list is not one.
  // Kept while one is applied, so an address that asks for it can still be seen and cleared.
  const ownersKnown = items.some((f) => f.owner !== undefined) || filters.owner !== null
  const standardsKnown = items.some((f) => f.scubaReference !== undefined) || filters.standard !== null

  /*
   * NOT LICENSED, on an area page (0158, owner 2026-09-26).
   *
   * An area whose every control reads a service the tenant holds no licence for -- and which
   * Microsoft refused to read -- finishes without failing, and then this page used to say "No open
   * findings in Email." over an Email nobody could read: the sentence a clean Email gets. So:
   *  - wholly not licensed: the empty state says the area is not assessed, and why, in place of
   *    "No open findings" -- whatever severity pill is lit, since "No open High findings in Email"
   *    is the same all-clear, narrowed;
   *  - partly not licensed (Endpoint: Intune assessed, Defender not licensed): a note above the
   *    list names what was not assessed, and the empty state speaks only for the parts that were.
   * The note also stands above a wholly unlicensed area that still has rows -- findings from before
   * a licence lapsed, which stay open because a control that did not run closes nothing.
   */
  const licenceGroups = area ? notLicensedIn(data.freshness, area) : []
  const wholeNotLicensed = area ? wholeAreaNotLicensed(data.freshness, area) : false
  const partlyNotLicensed = !wholeNotLicensed && licenceGroups.length > 0

  // The empty state carries the "not assessed" sentence itself only in the active view -- the
  // set-aside view's empty state is about decisions people made, not about the scan -- so the note
  // above steps aside only when that sentence is on screen, rather than saying it twice.
  const emptySaysNotAssessed = wholeNotLicensed && view === 'active' && items.length === 0
  const showLicenceNote =
    area !== undefined && (partlyNotLicensed || (wholeNotLicensed && !emptySaysNotAssessed))

  const filtersShown = filtersOpen || filtered

  // One finding as a row of the table: the flat orders, and a check By check that found one resource.
  const findingRow = (finding: FindingSummary) => (
                  <Row key={finding.id} role="row">
                    <Td data-label="Finding">
                      <button
                        type="button"
                        className="finding-row-title"
                        onClick={() => onOpen(finding)}
                      >
                        {finding.title}
                      </button>
                      {/* No reference and no check key under the title (owner's calls, 2026-09-25).
                          The reference identifies this finding to us and is on the finding's own
                          page; the check key meant nothing to a customer and stays in the CSV
                          export. What does go here is the line that tells two same-titled
                          findings apart. */}
                      {summaryLine(finding)}
                      {/* On an area page every row is that area, so the line names its project
                          instead: related findings are seen as one job even in a flat sort. */}
                      <span className="finding-row-meta">{area ? projectOf(finding) : finding.area}</span>
                    </Td>
                    <Td data-label="Severity">
                      {severityTag(finding.severity)}
                      {finding.severitySource && finding.severitySource !== 'defenssive' ? (
                        <span className="finding-sev-source">per {severitySourceLabel(finding.severitySource)}</span>
                      ) : null}
                    </Td>
                    <Td data-label="Open for"><OpenFor finding={finding} targets={targets} isNew={fresh.has(finding.id)} /></Td>
                    <Td data-label="Owner"><OwnerCell owner={finding.owner} /></Td>
                    <Td data-label="Effort">{effortChip(finding.effort)}</Td>
                  </Row>
  )

  return (
    <>
      <Freshness freshness={data.freshness} onScanQueued={() => setRescans((n) => n + 1)} />

      {showLicenceNote && area ? (
        <AreaNotLicensed area={area} freshness={data.freshness} />
      ) : null}

      {overviewData ? (
        <FindingsInsights
          area={area}
          overview={overviewData}
          changes={changes}
          focus={focus}
          onFocus={(next) => {
            setFocus(next)
            setPaging({ key, index: 0 })
          }}
          ownerNone={filters.owner === OWNER_NONE}
          onOwnerNone={() => setFilter('owner', filters.owner === OWNER_NONE ? null : OWNER_NONE)}
          areaTile={areaTile}
        />
      ) : null}

      {/* One row: what to look at, how severe, a search, the order, and the rest behind Filters. */}
      <div className="findings-filters findings-controls">
        <div className="findings-seg" role="group" aria-label="Show">
          <button
            type="button"
            className="findings-seg-btn"
            aria-pressed={view === 'active'}
            onClick={() => requery({ view: 'active' })}
          >
            Active
          </button>
          <button
            type="button"
            className="findings-seg-btn"
            aria-pressed={view === 'suppressed'}
            onClick={() => requery({ view: 'suppressed' })}
          >
            Set aside
          </button>
        </div>

        <div className="findings-chips" role="group" aria-label="Severity">
          <button
            type="button"
            className="findings-chip"
            aria-pressed={severity === null}
            aria-label="All severities"
            onClick={() => requery({ severity: null })}
          >
            All
          </button>
          {SEVERITY_FILTERS.map((value) => (
            <button
              key={value}
              type="button"
              className="findings-chip"
              aria-pressed={severity === value}
              onClick={() => requery({ severity: value })}
            >
              <i className={`findings-chip-swatch dv2-seg-${value.toLowerCase()}`} aria-hidden="true" />
              {value}
              {severityCounts && view === 'active' ? <span className="findings-chip-count"> {severityCounts[value] ?? 0}</span> : null}
            </button>
          ))}
        </div>

        <div className="findings-search">
          <label className="sr-only" htmlFor="finding-search">
            Search findings
          </label>

          <input
            ref={searchBox}
            id="finding-search"
            className="findings-search-input findings-search-name"
            type="search"
            value={typed}
            placeholder="Search findings, e.g. MFA"
            size={22}
            spellCheck={false}
            autoComplete="off"
            onChange={(event) => {
              setTyped(event.target.value)
              // A new search starts on its own first page: page 3 of the last matches may not
              // exist among these.
              setPaging({ key, index: 0 })
              forgetAsked()
            }}
          />
        </div>

        <div className="findings-order">
          {/* Impact × effort is the default and, on the Recommendations view, the grouped one:
              worst-first, then cheapest-first, so the quick wins surface. The other two are flat.
              On an area page the same order is By project: named for what it shows there, the
              area's findings grouped into the jobs that fix them. */}
          <label htmlFor={`${ids}-order`}>Order</label>
          <select
            id={`${ids}-order`}
            className="findings-search-input findings-order-select"
            value={sort}
            onChange={(e) => requery({ sort: e.target.value as Order })}
          >
            <option value="impact">{area ? 'By project' : 'Impact × effort'}</option>
            <option value="check">By check</option>
            <option value="severity">By severity</option>
            <option value="lastSeen">Most recent</option>
          </select>
          {showFacets ? (
            <button
              type="button"
              className="findings-filter findings-filters-toggle"
              aria-expanded={filtersShown}
              aria-controls={`${ids}-facets`}
              onClick={() => setFiltersOpen((o) => !o)}
            >
              Filters{filtered ? ' (on)' : ''}
            </button>
          ) : null}
        </div>
      </div>

      {/* The narrowing filters, over what has been loaded (findingFilters.ts). Real selects with
          real labels, so each is announced by name and changed from the keyboard like any other
          form control; each option carries how many findings it would show as things stand. */}
      {showFacets && filtersShown ? (
        <div ref={facetBar} id={`${ids}-facets`} className="findings-facets" role="group" aria-label="Filter the list">
          {area ? null : (
            <Facet id={`${ids}-area`} label="Area" which="area" items={items} filters={filters} search={matches} onChange={setFilter} />
          )}
          <Facet id={`${ids}-project`} label="Project" which="project" items={items} filters={filters} search={matches} onChange={setFilter} />
          {ownersKnown ? (
            <Facet id={`${ids}-owner`} label="Owner" which="owner" items={items} filters={filters} search={matches} onChange={setFilter} />
          ) : null}
          {standardsKnown ? (
            <Facet id={`${ids}-standard`} label="Standard" which="standard" items={items} filters={filters} search={matches} onChange={setFilter} />
          ) : null}
          {filtered ? (
            <button type="button" className="findings-filter" onClick={clearFilters}>
              Clear filters
            </button>
          ) : null}
        </div>
      ) : null}

      <p className="findings-updating" role="status" aria-live="polite">
        {pending ? 'Updating…' : ''}
      </p>

      {/* Filtering as you type, or by a select, changes the rows without a word; this is the word. */}
      <p className="findings-count" role="status" aria-live="polite">
        {narrowed && items.length > 0 ? matchLine(shown.length, items.length, matches ? searched : null, filtered, loadingRest) : ''}
      </p>

      <div ref={list} className="findings-paged" aria-busy={pending || undefined}>
      {items.length === 0 && pending ? null : items.length === 0 ? (
        <Card>
          {/* A statement about the filter, not about the tenant. The banner above is what speaks
              about the tenant, which is why it has to be on this page too. */}
          <p className="live-empty">
            {view === 'suppressed' ? (
              severity ? <>Nothing at {severity} severity has been set aside.</> : <>Nothing has been set aside.</>
            ) : emptySaysNotAssessed && area ? (
              // Not "No open findings in Email": nothing in Email was read. See the note above.
              <>
                {area} is not assessed: this tenant has {noLicenceFor(licenceGroups)}. Microsoft
                refused to let us read it, and nothing here is counted as passing.
              </>
            ) : severity ? (
              <>
                No open {severity} findings
                {area ? (partlyNotLicensed ? <> in the parts of {area} that were assessed</> : <> in {area}</>) : null}.
              </>
            ) : area ? (
              partlyNotLicensed
                ? <>No open findings in the parts of {area} that were assessed.</>
                : <>No open findings in {area}.</>
            ) : (
              <>No open findings.</>
            )}
            {/* The whole-tenant list: an area never read must not hide inside "none". */}
            {view === 'active' && !area && notAssessedAreasSentence(data.freshness)
              ? ` ${notAssessedAreasSentence(data.freshness)}`
              : ''}
            {view === 'active' && !emptySaysNotAssessed && !data.freshness.complete
              ? ' The assessment above is not complete, so this is not an all-clear.'
              : ''}
          </p>
        </Card>
      ) : shown.length === 0 ? (
        <Card>
          {/* Naming the search back matters: the list looks the same whether the search found nothing
              or the box was ignored. The reference sentence is the trap -- this list is filtered to
              OPEN findings, so a reference that has since been fixed matches nothing here and would
              otherwise read as "no such finding". */}
          <p className="live-empty">
            {matches && filtered ? (
              <>No findings match “{searched}” and the filters</>
            ) : matches ? (
              <>No findings match “{searched}”</>
            ) : (
              <>No findings match the filters</>
            )}
            {loadingRest ? ' among those loaded so far' : ''}.
            {matches && FINDING_REFERENCE.test(searched)
              ? ' If that is a reference you were sent and the finding has been fixed since, it will not be in this list.'
              : ''}
            {/* The whole-tenant list, narrowed to an area nobody could read, must not read as that
                area being clean either. */}
            {filtered && view === 'active' && !area && notAssessedAreasSentence(data.freshness)
              ? ` ${notAssessedAreasSentence(data.freshness)}`
              : ''}
            {view === 'active' && !data.freshness.complete
              ? ' The assessment above is not complete, so this is not an all-clear.'
              : ''}
          </p>
          <div className="findings-search-miss">
            {matches ? (
              <button type="button" className="findings-filter" onClick={clearSearch}>
                Clear search
              </button>
            ) : null}
            {matches && filtered ? ' ' : null}
            {filtered ? (
              <button type="button" className="findings-filter" onClick={clearFilters}>
                Clear filters
              </button>
            ) : null}
          </div>
        </Card>
      ) : grouped ? (
        <>
          {/* Do these first: high impact, low effort. Sits above the workstreams because the
              answer to "where do I even start" should not be a scroll away. Always open: it is
              already the short list. */}
          {quickWins.length > 0 ? (
            <Card style={{ marginBottom: 'var(--space-4)' }}>
              <div className="group-head">
                <h2 className="group-head-title">Start here</h2>
                <span className="group-head-hint">High impact, quick to do — worth clearing first.</span>
              </div>
              {quickWins.map((f) => (
                <div key={f.id} className="group-row">
                  {severityTag(f.severity)}
                  <div className="group-row-title">
                    <button type="button" className="finding-row-title" onClick={() => onOpen(f)}>
                      {f.title}
                    </button>
                    {summaryLine(f)}
                    <RowFacts finding={f} targets={targets} isNew={fresh.has(f.id)} />
                  </div>
                  {effortChip(f.effort)}
                </div>
              ))}
            </Card>
          ) : null}

          {/* One card per project: the job, not the individual findings. Ordered worst-first by
              the group's most severe member; the ungrouped "Other" bucket sinks to the end. */}
          {groups.map((g, index) => (
            <ProjectCard
              key={g.name}
              name={g.name}
              // The ordering caveat is advice for doing the work; set-aside findings are not being
              // worked, so on an area page's Set-aside view the heading carries none.
              hint={view === 'active' ? g.hint : null}
              findings={g.findings}
              restId={`${ids}-project-${index}`}
              open={isOpen(g.name)}
              onToggle={() => toggle(g.name)}
              onOpen={onOpen}
              targets={targets}
              fresh={fresh}
            />
          ))}
        </>
      ) : (
        <>
          {paged ? (
            <PageTabs count={pageCount} current={current} held={pending} where="top" onGo={goTo} />
          ) : null}

          <Card>
            {/* The region names the page, and focus moves here on a page change, so what a screen
                reader says on arrival is which page this now is. */}
            <TableScroll label={paged ? `Findings, page ${current + 1} of ${pageCount}` : 'Findings'}>
            <Table className="findings-table" role="table">
              <thead role="rowgroup">
                <Row role="row">
                  <Th>Finding</Th>
                  <Th>Severity</Th>
                  {/* How long it has been open, against its target, and who has it: what differs
                      between rows. Last seen -- the last assessment, the same on every row -- is on
                      the finding's page and in the CSV. */}
                  <Th>Open for</Th>
                  <Th>Owner</Th>
                  <Th>Effort</Th>
                </Row>
              </thead>
              <tbody role="rowgroup">
                {checkRows ? checkRows.map((group) =>
                  group.findings.length === 1 ? (
                    findingRow(group.findings[0]!)
                  ) : (
                    <CheckRows
                      key={group.key}
                      group={group}
                      area={area}
                      open={openChecks[group.key] ?? false}
                      onToggle={() => setOpenChecks((now) => ({ ...now, [group.key]: !(now[group.key] ?? false) }))}
                      onOpen={onOpen}
                      targets={targets}
                      fresh={fresh}
                      rowId={`${ids}-check-${group.key}`}
                    />
                  ),
                ) : rows.map((finding) => findingRow(finding))}
              </tbody>
            </Table>
            </TableScroll>
          </Card>

          {paged ? (
            <PageTabs count={pageCount} current={current} held={pending} where="bottom" onGo={goTo} />
          ) : null}
        </>
      )}
      </div>
      
      {/* The rows so far are shown while the rest arrive, with no tab for a page not yet loaded. */}
      <p className="findings-rest" role="status" aria-live="polite">
        {loadingRest ? 'Loading the rest…' : ''}
      </p>

      {/* The cap. Past it the counts, the pages, the groups, the search and the filters all
          describe the first part of the list only, so say so rather than letting it read as the
          whole. Only a severity narrows what is loaded -- the search and the selects look at what
          already was -- so that is the way to the rest this names. */}
      {capped ? (
        <p className="live-empty" style={{ marginTop: 'var(--space-2)' }}>
          {grouped
            ? `Showing the first ${items.length} findings, grouped. More remain — narrow the list by severity above to complete each project.`
            : `Showing the first ${items.length} findings. Narrow the list by severity above to see the rest.`}
          {matches && filtered
            ? ' The search and the filters look only at these.'
            : matches
              ? ' The search looks only at these.'
              : filtered
                ? ' The filters look only at these.'
                : ''}
        </p>
      ) : null}

      {moreFailed !== null ? (
        <div className="live-error" style={{ marginTop: 'var(--space-4)' }}>
          {moreFailed === 400
            ? 'This page reference expired before the rest of the list could load. Reload to see every finding — what is shown is not the whole list.'
            : describeFailure(moreFailed)}
        </div>
      ) : null}
    </>
  )
}

/**
 * What the latest scan did not assess in this area because the tenant is not licensed for it
 * (0158), above the area's list.
 *
 * Two cases, worded differently because they are different facts:
 *  - part of the area (Endpoint's Defender controls beside its Intune one): how many controls, and
 *    which licence, so the rows below -- or "No open findings in the parts of Endpoint that were
 *    assessed" -- are not read as the whole of Endpoint;
 *  - the whole area, while the page still has rows to show (findings from before a licence lapsed):
 *    the area was not assessed THIS time, and what is listed is from earlier assessments. They stay
 *    open, because a control that did not run closes nothing (owner, 2026-09-26).
 *
 * The same neutral card as the dashboard's: a service the tenant never bought is not a failure, and
 * the words, not an amber border, carry "not counted as passing".
 */
function AreaNotLicensed({ area, freshness }: { area: string; freshness: ScanFreshness }) {
  const groups = notLicensedIn(freshness, area)
  const whole = wholeAreaNotLicensed(freshness, area)
  const one = serviceNames(groups).length <= 1

  if (whole) {
    return (
      <Card className="unread-controls is-not-licensed">
        <h2 className="unread-controls-title">{area} was not assessed on the latest scan</h2>
        <p className="unread-controls-body">
          This tenant has {noLicenceFor(groups)}, and Microsoft refused to let us read{' '}
          {one ? 'it' : 'them'}. Nothing in {area} is counted as passing, and a finding from an earlier
          assessment stays open until an assessment can check it again.
        </p>
      </Card>
    )
  }

  if (groups.length === 0) return null

  const n = controlCount(groups)
  const names = serviceNames(groups)

  return (
    <Card className="unread-controls is-not-licensed">
      <h2 className="unread-controls-title">Part of {area} was not assessed</h2>
      <p className="unread-controls-body">
        {controlsWord(n)} here {n === 1 ? 'needs' : 'need'}{' '}
        {names.length === 0 ? 'a Microsoft service' : listOf(names, 'or')}. This tenant has no licence
        for {one ? 'it' : 'them'}, and Microsoft refused to let us read {one ? 'it' : 'them'}, so{' '}
        {n === 1 ? 'that control was' : 'those controls were'} not assessed.{' '}
        {n === 1 ? 'It is' : 'They are'} <strong>not</strong> counted as passing, and nothing{' '}
        {n === 1 ? 'it' : 'they'} found before has been closed.
      </p>
    </Card>
  )
}

/**
 * One project's card: its name on the grey band, how many findings it holds, and -- while closed --
 * only its most important one (leadFirst), with a button to show the rest.
 *
 * The button is a disclosure: aria-expanded says whether the rest are shown, aria-controls names the
 * region they appear in, and the project's name is in its accessible name, so twenty of them read
 * as twenty different buttons rather than twenty "Show all". It sits in the band, above what it
 * opens, and does not move when pressed. The region is always in the page, empty while closed, so
 * aria-controls never points at nothing.
 */
function ProjectCard({
  name,
  hint,
  findings,
  restId,
  open,
  onToggle,
  onOpen,
  targets,
  fresh,
}: {
  name: string
  hint: string | null
  findings: FindingSummary[]
  restId: string
  open: boolean
  onToggle: () => void
  onOpen: (finding: FindingSummary) => void
  targets?: Record<string, number>
  fresh?: Set<string>
}) {
  const [lead, ...rest] = leadFirst(findings)
  const count = findings.length

  return (
    <Card style={{ marginBottom: 'var(--space-3)' }}>
      {/* The project on a grey band, its findings on white below it. Group title and finding titles
          were the same weight and colour on the same white, so a card read as a list of equals
          rather than a job and its parts. A real heading, too. */}
      <div className="group-head">
        <h2 className="group-head-title">{name}</h2>
        <span className="group-head-count">
          {count} {count === 1 ? 'finding' : 'findings'}
        </span>
        {hint ? <span className="group-head-hint group-head-hint-end">{hint}</span> : null}
        {rest.length > 0 ? (
          <button
            type="button"
            className="findings-filter group-toggle"
            aria-expanded={open}
            aria-controls={restId}
            onClick={onToggle}
          >
            {open ? 'Show fewer' : `Show all ${count}`}
            <span className="sr-only"> findings in {name}</span>
          </button>
        ) : null}
      </div>
      {lead ? groupRow(lead, onOpen, targets, fresh) : null}
      <div id={restId}>{open ? rest.map((f) => groupRow(f, onOpen, targets, fresh)) : null}</div>
    </Card>
  )
}

/** A finding in a project card: title and summary, how long open and who has it, severity, effort. */
function groupRow(
  f: FindingSummary,
  onOpen: (finding: FindingSummary) => void,
  targets?: Record<string, number>,
  fresh?: Set<string>,
) {
  return (
    <div key={f.id} className="group-row">
      <div className="group-row-title">
        <button type="button" className="finding-row-title" onClick={() => onOpen(f)}>
          {f.title}
        </button>
        {summaryLine(f)}
        <RowFacts finding={f} targets={targets} isNew={fresh?.has(f.id) ?? false} />
      </div>
      {severityTag(f.severity)}
      {effortChip(f.effort)}
    </div>
  )
}

/** "Open for 34 days", in the critical colour with the target it passed, and "New" for what the latest
 *  assessment added or brought back. */
function OpenFor({ finding, targets, isNew }: { finding: FindingSummary; targets?: Record<string, number>; isNew: boolean }) {
  const days = daysOpen(finding.firstSeenAt)
  const passed = passedTarget(finding, targets)
  return (
    <span className={passed !== null ? 'finding-age finding-age-late' : 'finding-age'}>
      {days === 0 ? 'Today' : days === 1 ? '1 day' : `${days} days`}
      {isNew ? <span className="finding-new">New</span> : null}
      {passed !== null ? <span className="finding-age-target">past the {passed}-day target</span> : null}
    </span>
  )
}

/** The owner, or "No owner". Nothing from an older server that does not send the field. */
function OwnerCell({ owner }: { owner: string | null | undefined }) {
  if (owner === undefined) return null
  return owner ? <span className="finding-owner">{owner}</span> : <span className="finding-owner finding-owner-none">No owner</span>
}

/** The same facts on one line, for the grouped views' rows. */
function RowFacts({ finding, targets, isNew }: { finding: FindingSummary; targets?: Record<string, number>; isNew: boolean }) {
  const days = daysOpen(finding.firstSeenAt)
  const passed = passedTarget(finding, targets)
  return (
    <span className="finding-row-facts">
      <span className={passed !== null ? 'finding-age-late' : undefined}>
        Open for {days === 1 ? '1 day' : `${days} days`}
        {passed !== null ? `, past the ${passed}-day target` : ''}
      </span>
      {finding.owner !== undefined ? <> · {finding.owner ?? 'No owner'}</> : null}
      {isNew ? <span className="finding-new">New</span> : null}
    </span>
  )
}

/**
 * One narrowing filter: a labelled select whose options carry what each would show (facetOptions).
 * "All" first, meaning the filter is off; choosing it takes the filter out of the address.
 */
function Facet({
  id,
  label,
  which,
  items,
  filters,
  search,
  onChange,
}: {
  id: string
  label: string
  which: FilterKey
  items: FindingSummary[]
  filters: ListFilters
  search: ((finding: FindingSummary) => boolean) | null
  onChange: (which: FilterKey, value: string | null) => void
}) {
  const { all, options } = facetOptions(items, filters, which, search)
  return (
    <div className="findings-facet">
      <label className="findings-search-label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="findings-search-input findings-facet-select"
        value={filters[which] ?? ''}
        onChange={(event) => onChange(which, event.target.value === '' ? null : event.target.value)}
      >
        <option value="">All ({all})</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label} ({option.count})
          </option>
        ))}
      </select>
    </div>
  )
}

/**
 * The line that says how many findings a search or the filters left. A search alone keeps the
 * sentence it always had; the filters say how many of how many, since "3 findings match the
 * filters" does not say whether that is most of the list or a sliver of it.
 */
function matchLine(
  shown: number,
  loaded: number,
  searched: string | null,
  filtered: boolean,
  soFar: boolean,
): string {
  const verb = shown === 1 ? 'matches' : 'match'
  const tail = soFar ? ' so far' : ''
  if (searched !== null && !filtered) {
    return `${shown} ${shown === 1 ? 'finding matches' : 'findings match'} “${searched}”${tail}.`
  }
  const of = `${shown} of ${loaded} ${loaded === 1 ? 'finding' : 'findings'}`
  return searched !== null
    ? `${of} ${verb} “${searched}” and the filters${tail}.`
    : `${of} ${verb} the filters${tail}.`
}

/**
 * Numbered page tabs: « Previous · 1 … 4 5 6 … 12 · Next ».
 *
 * Drawn above and below the table. Real buttons in a labelled nav, the current page marked with
 * aria-current, so the tabs read as a set of pages and not as twelve unexplained numbers.
 */
function PageTabs({
  count,
  current,
  held,
  where,
  onGo,
}: {
  count: number
  current: number
  /** A new query is on its way; paging the old one's rows is not what was asked for. */
  held: boolean
  where: 'top' | 'bottom'
  onGo: (index: number, from: 'top' | 'bottom') => void
}) {
  const go = (index: number) => onGo(index, where)
  return (
    <nav className={where === 'bottom' ? 'findings-pages findings-pages-bottom' : 'findings-pages'} aria-label="Pages">
      <button
        type="button"
        className="findings-page findings-page-step"
        disabled={held || current === 0}
        onClick={() => go(current - 1)}
      >
        <span aria-hidden="true">«</span> Previous
      </button>

      {pageTabs(count, current).map((entry) =>
        typeof entry === 'number' ? (
          <button
            key={entry}
            type="button"
            className="findings-page"
            aria-current={entry === current ? 'page' : undefined}
            disabled={held}
            onClick={() => go(entry)}
          >
            {entry + 1}
          </button>
        ) : (
          <span key={entry} className="findings-page-gap" aria-hidden="true">…</span>
        ),
      )}

      <button
        type="button"
        className="findings-page findings-page-step"
        disabled={held || current === count - 1}
        onClick={() => go(current + 1)}
      >
        Next <span aria-hidden="true">»</span>
      </button>
    </nav>
  )
}

/**
 * Which page tabs to draw: the first, the last, and the current page with its neighbours, with an
 * ellipsis over each gap. A gap of exactly one page draws that page instead -- an ellipsis standing
 * in for a single number saves nothing and hides where it goes.
 */
function pageTabs(count: number, current: number): (number | 'gap-before' | 'gap-after')[] {
  const wanted = [...new Set([0, current - 1, current, current + 1, count - 1])]
    .filter((i) => i >= 0 && i < count)
    .sort((a, b) => a - b)

  const out: (number | 'gap-before' | 'gap-after')[] = []
  let last = -1
  for (const i of wanted) {
    if (i - last === 2) out.push(i - 1)
    else if (i - last > 2) out.push(i < current ? 'gap-before' : 'gap-after')
    out.push(i)
    last = i
  }
  return out
}

/**
 * The search, as a test over loaded findings, or null when the box is empty.
 *
 * By NAME -- the title, and the one-line summary under it -- because that is what a person reads on
 * the list and remembers. Text shaped like a reference ALSO matches the finding it names, so one
 * pasted from an email still finds its row although the list no longer prints references. A
 * reference is the start of the finding's id (referenceOf), so matching the start of the id covers
 * the eight printed characters and a longer paste or a whole id alike.
 */
function matcherFor(text: string): ((finding: FindingSummary) => boolean) | null {
  const q = text.trim().toLowerCase()
  if (q === '') return null

  const reference = FINDING_REFERENCE.test(q) ? q.replace(/-/g, '') : null

  return (finding) =>
    finding.title.toLowerCase().includes(q) ||
    (finding.summary ?? '').toLowerCase().includes(q) ||
    (reference !== null && finding.id.replace(/-/g, '').toLowerCase().startsWith(reference))
}

/**
 * Each finding once. Pages are fetched one after another while an assessment may be writing, and a
 * finding re-observed between two requests can move across a page boundary and arrive twice -- two
 * rows for one finding, and two React children with one key.
 */
function distinct(findings: FindingSummary[]): FindingSummary[] {
  const seen = new Set<string>()
  return findings.filter((f) => {
    if (seen.has(f.id)) return false
    seen.add(f.id)
    return true
  })
}

/** The quiet line under a title that tells same-titled findings apart. Nothing when there is none. */
function summaryLine(finding: FindingSummary) {
  return <FindingScope summary={finding.summary} />
}

export function formatDate(value: string): string {
  const when = new Date(value)
  return Number.isNaN(when.getTime())
    ? value
    : when.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

/** Worst first. Unknown severities sink. */
const SEVERITY_RANK: Record<string, number> = {
  Critical: 0,
  High: 1,
  Medium: 2,
  Low: 3,
  Informational: 4,
}

/**
 * The effort chip. A quiet outline, not a fill — effort is context for the severity beside it, not
 * a claim of its own. Reads "Low effort" / "Medium effort" / "High effort".
 */
export function effortChip(effort: string) {
  return (
    <span
      style={{
        flex: 'none',
        border: '1px solid var(--color-neutral-300)',
        color: 'var(--color-neutral-600)',
        background: 'var(--color-surface)',
        fontSize: 14,
        padding: '2px 9px',
        borderRadius: 3,
        whiteSpace: 'nowrap',
      }}
    >
      {effort} effort
    </span>
  )
}

/**
 * Partition findings into their workstreams, worst-first.
 *
 * Grouping is done on whatever the page loaded rather than in the query, because the ordering that
 * pages the list (impact) and the grouping are different shapes — you cannot keyset-page a grouped
 * list. The list loads every page its filter matches (up to the cap, which it states), so this sees
 * the whole set. The ungrouped bucket ("Other") always sinks to the end; a group inherits its
 * ordering caveat from the first member that carries one (they all carry the same one).
 */
function groupByWorkstream(
  items: FindingSummary[],
): { name: string; hint: string | null; findings: FindingSummary[] }[] {
  const byName = new Map<string, FindingSummary[]>()
  for (const f of items) {
    const name = projectOf(f)
    const bucket = byName.get(name)
    if (bucket) bucket.push(f)
    else byName.set(name, [f])
  }

  const worst = (findings: FindingSummary[]) =>
    Math.min(...findings.map((f) => SEVERITY_RANK[f.severity] ?? 9))

  return [...byName.entries()]
    .map(([name, findings]) => ({
      name,
      hint: findings.find((f) => f.sequenceHint)?.sequenceHint ?? null,
      findings,
    }))
    .sort((a, b) => {
      // The ungrouped bucket is not a job; it goes last whatever it contains.
      if (a.name === OTHER_PROJECT) return 1
      if (b.name === OTHER_PROJECT) return -1
      return worst(a.findings) - worst(b.findings)
    })
}

/** A check and the findings it raised, By check. */
interface CheckGroup {
  key: string
  findings: FindingSummary[]
}

/**
 * One group per check (catalogue key), in the order the list already has (severity first), each group
 * placed where its most severe finding falls, its findings worst first.
 */
export function groupByCheck(findings: FindingSummary[]): CheckGroup[] {
  const groups = new Map<string, FindingSummary[]>()
  for (const f of findings) {
    const list = groups.get(f.catalogKey)
    if (list) list.push(f)
    else groups.set(f.catalogKey, [f])
  }
  return [...groups.entries()].map(([key, list]) => ({ key, findings: list }))
}

/** The noun for what a check's findings are about: resources on Cloud, findings elsewhere. */
function affected(area: string | undefined, n: number): string {
  if (area === 'Cloud') return n === 1 ? '1 resource' : `${n} resources`
  return n === 1 ? '1 finding' : `${n} findings`
}

/**
 * A check that found several resources: one row for the check -- its worst severity, its oldest open,
 * who has its findings -- and, opened, a row per resource named by its own summary line.
 */
function CheckRows({
  group,
  area,
  open,
  onToggle,
  onOpen,
  targets,
  fresh,
  rowId,
}: {
  group: CheckGroup
  area?: string
  open: boolean
  onToggle: () => void
  onOpen: (finding: FindingSummary) => void
  targets?: Record<string, number>
  fresh: Set<string>
  rowId: string
}) {
  const worst = [...group.findings].sort((a, b) => (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9))[0]!
  const oldest = [...group.findings].sort((a, b) => Date.parse(a.firstSeenAt) - Date.parse(b.firstSeenAt))[0]!
  const owners = new Set(group.findings.map((f) => f.owner ?? null))
  const ownersKnown = group.findings.some((f) => f.owner !== undefined)
  const anyNew = group.findings.some((f) => fresh.has(f.id))
  const label = `${worst.title}: ${affected(area, group.findings.length)}`

  return (
    <>
      <Row className="check-group-row" role="row">
        <Td data-label="Finding">
          <div className="check-group-head">
            <button
              type="button"
              className="check-toggle"
              aria-expanded={open}
              aria-controls={rowId}
              aria-label={`${open ? 'Hide' : 'Show'} the ${affected(area, group.findings.length)} for ${worst.title}`}
              onClick={onToggle}
            >
              <span aria-hidden="true">{open ? '▾' : '▸'}</span>
            </button>
            <div>
              <button type="button" className="finding-row-title" onClick={onToggle} aria-label={label}>
                {worst.title}
              </button>
              <span className="finding-row-meta">
                {affected(area, group.findings.length)} · {area ? projectOf(worst) : worst.area}
              </span>
            </div>
          </div>
        </Td>
        <Td data-label="Severity">{severityTag(worst.severity)}</Td>
        <Td data-label="Open for"><OpenFor finding={oldest} targets={targets} isNew={anyNew} /></Td>
        <Td data-label="Owner">
          {!ownersKnown ? null : owners.size === 1 ? (
            <OwnerCell owner={[...owners][0]} />
          ) : (
            <span className="finding-owner">{owners.has(null) ? 'Some have no owner' : `${owners.size} owners`}</span>
          )}
        </Td>
        <Td data-label="Effort">{effortChip(worst.effort)}</Td>
      </Row>
      {open
        ? group.findings.map((f, i) => (
            <Row key={f.id} className="check-member-row" role="row" id={i === 0 ? rowId : undefined}>
              <Td data-label="Finding">
                <button type="button" className="finding-row-title check-member-title" onClick={() => onOpen(f)}>
                  {/* Its own line names the resource. A finding written before the check sent one
                      says which of the group it is rather than repeating the title above it. */}
                  {f.summary ?? `${area === 'Cloud' ? 'Resource' : 'Finding'} ${i + 1} of ${group.findings.length}`}
                </button>
                {f.summary ? null : <span className="finding-row-meta">Named after the next assessment. Open it to see which one.</span>}
              </Td>
              <Td data-label="Severity">{severityTag(f.severity)}</Td>
              <Td data-label="Open for"><OpenFor finding={f} targets={targets} isNew={fresh.has(f.id)} /></Td>
              <Td data-label="Owner"><OwnerCell owner={f.owner} /></Td>
              <Td data-label="Effort">{effortChip(f.effort)}</Td>
            </Row>
          ))
        : null}
    </>
  )
}
